const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function wireModalAriaLabel(modalEl, modalId, fallbackTitle = "") {
  if (!(modalEl instanceof HTMLElement)) {
    return;
  }

  const labelledBy = modalEl.getAttribute("aria-labelledby");
  if (labelledBy && document.getElementById(labelledBy)) {
    return;
  }

  const explicitTitle = String(fallbackTitle ?? "").trim();
  if (explicitTitle) {
    let titleEl = document.getElementById(`${modalId}Label`);
    if (!titleEl) {
      titleEl = document.createElement("h2");
      titleEl.id = `${modalId}Label`;
      titleEl.className = "visually-hidden";
      titleEl.textContent = explicitTitle;
      modalEl.insertBefore(titleEl, modalEl.firstChild);
    }
    modalEl.setAttribute("aria-labelledby", titleEl.id);
    modalEl.removeAttribute("aria-label");
    return;
  }

  const heading = modalEl.querySelector(
    ".modal-title, .modal-body h1, .modal-body h2, .modal-body h3, h1, h2, h3"
  );
  if (heading instanceof HTMLElement) {
    if (!heading.id) {
      heading.id = `${modalId}Label`;
    }
    modalEl.setAttribute("aria-labelledby", heading.id);
    modalEl.removeAttribute("aria-label");
    return;
  }

  modalEl.setAttribute("aria-label", "Dialog");
  modalEl.removeAttribute("aria-labelledby");
}

export function focusInitialModalElement(modalEl) {
  if (!(modalEl instanceof HTMLElement)) {
    return;
  }

  const preferred =
    modalEl.querySelector(".btn-close, .btn-close-roi, button, [href], input, select, textarea") ||
    modalEl.querySelector(FOCUSABLE_SELECTOR);

  if (preferred instanceof HTMLElement) {
    preferred.focus();
    return;
  }

  modalEl.focus();
}

export function trapFocusInModal(modalEl, event) {
  if (!(modalEl instanceof HTMLElement) || event.key !== "Tab") {
    return;
  }

  const focusables = Array.from(modalEl.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => el instanceof HTMLElement && !el.hasAttribute("disabled") && el.offsetParent !== null
  );

  if (focusables.length === 0) {
    event.preventDefault();
    modalEl.focus();
    return;
  }

  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement;

  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

export function setModalDialogOpenState(modalEl, isOpen) {
  if (!(modalEl instanceof HTMLElement)) {
    return;
  }

  modalEl.setAttribute("role", "dialog");

  if (isOpen) {
    modalEl.setAttribute("aria-modal", "true");
    modalEl.setAttribute("aria-hidden", "false");
    wireModalAriaLabel(modalEl, modalEl.id);
    focusInitialModalElement(modalEl);
    return;
  }

  modalEl.setAttribute("aria-hidden", "true");
  modalEl.removeAttribute("aria-modal");
}

export function bindBootstrapModalAccessibility(modalEl) {
  if (!(modalEl instanceof HTMLElement)) {
    return () => {};
  }

  const handleShow = () => {
    setModalDialogOpenState(modalEl, true);
  };

  const handleHidden = () => {
    setModalDialogOpenState(modalEl, false);
  };

  const handleKeyDown = (event) => trapFocusInModal(modalEl, event);

  modalEl.addEventListener("show.bs.modal", handleShow);
  modalEl.addEventListener("hidden.bs.modal", handleHidden);
  modalEl.addEventListener("keydown", handleKeyDown);

  return () => {
    modalEl.removeEventListener("show.bs.modal", handleShow);
    modalEl.removeEventListener("hidden.bs.modal", handleHidden);
    modalEl.removeEventListener("keydown", handleKeyDown);
  };
}

export function openManualDialog(modalEl) {
  if (!(modalEl instanceof HTMLElement)) {
    return;
  }

  modalEl.style.display = "flex";
  setModalDialogOpenState(modalEl, true);
}

export function closeManualDialog(modalEl) {
  if (!(modalEl instanceof HTMLElement)) {
    return;
  }

  modalEl.style.display = "none";
  setModalDialogOpenState(modalEl, false);
}
